import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getApiBaseUrl } from "../config/apiBase";
import { AuthenticatedFileLinks } from "./AuthenticatedFileLinks";
import { useAppState } from "../context/AppStateContext";
import { readFileAsRawBase64 } from "../utils/readFileAsRawBase64";
import { findSubcontractorMaster, isSubcontractorLinkageComplete } from "../utils/subcontractor";

export function SubcontractorAssignModal({
  open,
  applicationId,
  onClose,
}: {
  open: boolean;
  applicationId: string | null;
  onClose: () => void;
}) {
  const { getById, subcontractorMasters, updateSubcontractor, finalizeSubcontractor } = useAppState();
  const [linkError, setLinkError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const app = applicationId ? getById(applicationId) : undefined;

  useEffect(() => {
    if (!open) setLinkError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !applicationId || !app) return null;

  const link = app.subcontractor;
  const selectedMaster = findSubcontractorMaster(subcontractorMasters, link.subcontractorMasterId);
  const workOrderFileApiBase = `${getApiBaseUrl()}/api/applications/${encodeURIComponent(app.id)}/work-order-file`;

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal modal--assign-subcontractor"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sub-assign-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="sub-assign-modal-title">Assign subcontractor</h2>
        <p className="modal__lede">
          <span className="table-name">{app.mobile.beneficiaryName}</span>
          <span className="text-muted" aria-hidden="true">
            {" "}
            ·{" "}
          </span>
          <span className="mono text-muted">{app.mobile.applicationId}</span>
        </p>

        {linkError ? (
          <p className="alert alert--danger" role="alert">
            {linkError}
          </p>
        ) : null}

        <h3 className="form-section-title">Registered firm</h3>
        <div className="field field--full">
          <label htmlFor="sub-assign-master-select">Onboarded subcontractor</label>
          <select
            id="sub-assign-master-select"
            value={link.subcontractorMasterId ?? ""}
            onChange={(e) => {
              setLinkError(null);
              const v = e.target.value;
              updateSubcontractor(app.id, {
                subcontractorMasterId: v === "" ? null : v,
              });
            }}
          >
            <option value="">— Select an onboarded firm —</option>
            {subcontractorMasters.map((m) => (
              <option key={m.id} value={m.id}>
                {m.firmName}
                {m.contactPhone ? ` · ${m.contactPhone}` : ""}
              </option>
            ))}
          </select>
          <p className="text-muted" style={{ marginTop: "0.35rem", fontSize: "0.8125rem" }}>
            Missing a firm?{" "}
            <Link to="/subcontractor-onboarding" className="btn btn--link btn--sm" style={{ padding: 0, verticalAlign: "baseline" }}>
              Subcontractor Onboarding
            </Link>
          </p>
        </div>

        {selectedMaster ? (
          <div className="subcontractor-firm-panel subcontractor-firm-panel--compact">
            <h4 className="form-section-title">Selected firm (read-only)</h4>
            <dl className="summary-dl">
              <dt>Authorised contact</dt>
              <dd>{selectedMaster.contactPersonName}</dd>
              <dt>Registration / GSTIN</dt>
              <dd className="mono">
                {selectedMaster.firmRegistrationId.trim() || selectedMaster.gstNumber.trim() || "—"}
              </dd>
            </dl>
          </div>
        ) : null}

        <h3 className="form-section-title">Engagement for this beneficiary</h3>
        <div className="field field--full">
          <label htmlFor="sub-assign-work-order">Work order / engagement letter (scan)</label>
          <input
            id="sub-assign-work-order"
            ref={fileInputRef}
            type="file"
            accept=".pdf,image/*"
            onChange={(e) => {
              setLinkError(null);
              const f = e.target.files?.[0];
              updateSubcontractor(app.id, { workOrderFileName: f?.name ?? null });
            }}
          />
          {link.workOrderFileName ? (
            <span className="text-muted file-hint">On file: {link.workOrderFileName}</span>
          ) : null}
          {link.workOrderFileStored ? (
            <div className="text-muted file-hint" style={{ marginTop: "0.5rem" }}>
              <AuthenticatedFileLinks
                fileUrlBase={workOrderFileApiBase}
                fallbackFileName={link.workOrderFileName ?? "work-order"}
              />
            </div>
          ) : null}
        </div>

        {link.workOrderFileStored ? (
          <p className="text-muted" style={{ fontSize: "0.8125rem", marginBottom: 0 }}>
            Work order is on the server (Download / View above). Or pick a new file. Full screen:{" "}
            <Link to={`/subcontractor-to-beneficiary/${app.id}`}>Open</Link>.
          </p>
        ) : (
          <p className="text-muted" style={{ fontSize: "0.8125rem", marginBottom: 0 }}>
            Required: a firm from the list. Work order file is optional. Use{" "}
            <Link to={`/subcontractor-to-beneficiary/${app.id}`}>Open</Link> for the full linkage screen if needed.
          </p>
        )}

        <div className="modal__actions">
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary btn--tg"
            onClick={() => {
              void (async () => {
                setLinkError(null);
                const master = findSubcontractorMaster(subcontractorMasters, link.subcontractorMasterId);
                let filePayload: { workOrderFileBase64: string; workOrderMimeType: string | null } | undefined;
                const f = fileInputRef.current?.files?.[0];
                if (f) {
                  try {
                    const r = await readFileAsRawBase64(f);
                    filePayload = { workOrderFileBase64: r.base64, workOrderMimeType: r.mimeType };
                  } catch {
                    setLinkError("Could not read the selected file.");
                    return;
                  }
                }
                const effective = filePayload ? { ...link, ...filePayload } : link;
                if (!isSubcontractorLinkageComplete(effective, master)) {
                  setLinkError(
                    "Choose a subcontractor from the onboarded list. Register the firm under Subcontractor Onboarding first if it is not listed.",
                  );
                  return;
                }
                const ok = await finalizeSubcontractor(app.id, filePayload);
                if (ok) onClose();
                else
                  setLinkError(
                    "Choose a subcontractor from the onboarded list. Register the firm under Subcontractor Onboarding first if it is not listed.",
                  );
              })();
            }}
          >
            {app.status === "subcontractor_linked" ? "Update linkage" : "Record subcontractor"}
          </button>
        </div>
      </div>
    </div>
  );
}
